import 'maplibre-gl/dist/maplibre-gl.css';
import './styles.css';

import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
import {
  ApiError,
  fetchArea,
  fetchNetworks,
  fetchStops,
  fetchTrip,
  type BBox,
  type StopHit,
} from './api';
import { startAnalytics } from './analytics';
import { PRODUCT_ORDER } from './format';
import { createMap, styleUrl, type Theme } from './map/basemap';
import { TripLayer } from './map/tripLayer';
import { VehicleLayer, type ColorMode } from './map/vehicleLayer';
import type { MatomoConfig, NetworkInfo, Product, Vehicle } from './types';
import { DetailsPanel } from './ui/details';
import { ModeFilters } from './ui/filters';
import { SearchPanel, type LineHit } from './ui/search';
import { StatusBar } from './ui/statusbar';
import { Tooltip } from './ui/tooltip';

const STORAGE_KEY = 'infralivemap.prefs';

interface Prefs {
  theme?: Theme;
  colorMode?: ColorMode;
}

function loadPrefs(): Prefs {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Prefs;
  } catch {
    return {};
  }
}

function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Private browsing: preferences simply do not persist.
  }
}

/**
 * `#zoom/lat/lon` haelt die Ansicht teilbar und wiederherstellbar.
 *
 * Aeltere Verweise tragen als vierten Teil noch ein Netz. Er wird gelesen und
 * verworfen, damit solche Verweise weiterhin die richtige Stelle zeigen, statt
 * an einem unerwarteten Format zu scheitern.
 */
function readHash(): { zoom?: number; center?: [number, number] } {
  const parts = location.hash.replace(/^#/, '').split('/');
  const zoom = Number.parseFloat(parts[0] ?? '');
  const lat = Number.parseFloat(parts[1] ?? '');
  const lon = Number.parseFloat(parts[2] ?? '');
  if (!Number.isFinite(zoom) || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return {};
  }
  return { zoom, center: [lon, lat] };
}

function debounce<T extends (...args: never[]) => void>(fn: T, ms: number): T {
  let timer = 0;
  return ((...args: never[]) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), ms);
  }) as T;
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
}

async function boot(): Promise<void> {
  const prefs = loadPrefs();
  const hash = readHash();

  const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
  let theme: Theme = prefs.theme ?? (prefersLight ? 'light' : 'dark');
  document.documentElement.dataset.theme = theme;

  const statusBar = new StatusBar(el('statusbar'));

  let catalogue: { networks: NetworkInfo[]; pollIntervalMs: number; matomo: MatomoConfig | null };
  try {
    catalogue = await fetchNetworks();
  } catch (err) {
    statusBar.set({
      state: 'error',
      message: `Server nicht erreichbar: ${err instanceof Error ? err.message : String(err)}`,
    });
    return;
  }

  startAnalytics(catalogue.matomo);

  const networks = catalogue.networks;
  const pollIntervalMs = Math.max(3_000, catalogue.pollIntervalMs);
  if (networks.length === 0) {
    statusBar.set({ state: 'error', message: 'Keine Datenquelle konfiguriert.' });
    return;
  }
  // Keine Netzauswahl mehr: der Ausschnitt allein bestimmt, was zu sehen ist.
  const HOME: [number, number] = [7.4653, 51.5136];
  const HOME_ZOOM = 11;

  // --- Map -----------------------------------------------------------------

  const map: MapLibreMap = createMap(
    el('map'),
    theme,
    hash.center ?? HOME,
    hash.zoom ?? HOME_ZOOM,
  );
  const vehicleLayer = new VehicleLayer(map);
  const tripLayer = new TripLayer(map);
  const tooltip = new Tooltip(el('tooltip'));

  // --- View state ----------------------------------------------------------

  let colorMode: ColorMode = prefs.colorMode ?? 'line';
  // Nur eine ausgewaehlte Linie blendet ab - das blosse Tippen tut es nicht mehr.
  let lineFilter: string | null = null;
  let selectedId: string | null = null;
  let following = false;
  let programmaticMove = false;

  vehicleLayer.setColorMode(colorMode);

  const matchesLine = (vehicle: Vehicle): boolean =>
    lineFilter === null || vehicle.line.name.toLowerCase() === lineFilter;

  const filters = new ModeFilters(el('filters'), () => {
    applyView();
    refreshStats();
  });

  function applyView(): void {
    vehicleLayer.setView({
      visible: (vehicle) => filters.has(vehicle.line.product),
      highlighted: matchesLine,
    });
  }
  applyView();

  // --- Detail panel --------------------------------------------------------

  const details = new DetailsPanel(el('details'), {
    onClose: () => selectVehicle(null),
    onToggleFollow: () => {
      following = !following;
      details.setFollowing(following);
      if (following) centerOnSelected(600);
    },
    onRetryTrip: () => {
      const vehicle = selectedId ? vehicleLayer.getVehicle(selectedId) : null;
      if (!selectedId || !vehicle) return;
      details.open(vehicle, following);
      loadTrip(selectedId, vehicle.network, vehicle.line.bg);
    },
    onFitRoute: () => {
      const bounds = tripLayer.bounds();
      if (!bounds) return;
      following = false;
      details.setFollowing(false);
      programmaticMove = true;
      map.fitBounds(bounds, { padding: { top: 170, bottom: 90, left: 90, right: 400 } });
    },
  });

  let tripRequest: AbortController | null = null;

  function loadTrip(id: string, network: string, color: string): void {
    tripRequest?.abort();
    const controller = new AbortController();
    tripRequest = controller;
    fetchTrip(network, id, controller.signal)
      .then((trip) => {
        if (controller.signal.aborted || selectedId !== id) return;
        details.setTrip(trip);
        tripLayer.show(trip, color);
      })
      .catch((err) => {
        if (controller.signal.aborted || selectedId !== id) return;
        details.setError(
          err instanceof ApiError && err.status === 404
            ? 'Für diese Fahrt liegt kein Fahrtverlauf vor.'
            : `Fahrtverlauf konnte nicht geladen werden (${
                err instanceof Error ? err.message : String(err)
              }).`,
        );
      });
  }

  function selectVehicle(id: string | null): void {
    // Eine neue Auswahl - auch das Abwaehlen - beendet eine laufende Linienansicht.
    // Sonst blieben Fahrzeuge abgeblendet, ohne dass noch etwas darauf hinweist.
    if (lineFilter !== null) {
      lineFilter = null;
      applyView();
      refreshStats();
    }
    selectedId = id;
    following = false;
    vehicleLayer.setSelected(id);
    tripRequest?.abort();
    tripRequest = null;

    if (!id) {
      details.close();
      tripLayer.clear();
      updateHash();
      return;
    }

    const vehicle = vehicleLayer.getVehicle(id);
    if (!vehicle) return;
    details.open(vehicle, following);
    tripLayer.clear();

    if (!networks.find((n) => n.id === vehicle.network)?.tripDetails) {
      details.setError('Diese Datenquelle liefert keine Fahrtverläufe.');
      return;
    }

    loadTrip(id, vehicle.network, vehicle.line.bg);
  }

  function centerOnSelected(duration: number): void {
    if (!selectedId) return;
    const position = vehicleLayer.getPosition(selectedId);
    if (!position) return;
    programmaticMove = true;
    map.easeTo({ center: position, duration, easing: (t) => t });
  }

  // --- Polling -------------------------------------------------------------

  let pollRequest: AbortController | null = null;
  let lastUpdatedAt: number | null = null;
  let consecutiveErrors = 0;

  function viewportBBox(): BBox {
    const bounds = map.getBounds();
    const south = bounds.getSouth();
    const north = bounds.getNorth();
    const west = bounds.getWest();
    const east = bounds.getEast();
    // Fetch a little beyond the screen so panning does not reveal empty space.
    const padLat = (north - south) * 0.15;
    const padLon = (east - west) * 0.15;
    return {
      south: Math.max(-85, south - padLat),
      north: Math.min(85, north + padLat),
      west: Math.max(-180, west - padLon),
      east: Math.min(180, east + padLon),
    };
  }

  function limitForZoom(): number {
    const zoom = map.getZoom();
    if (zoom >= 14) return 500;
    if (zoom >= 12) return 900;
    return 1200;
  }

  function refreshStats(): void {
    const all = vehicleLayer.vehicles();
    const counts = Object.fromEntries(PRODUCT_ORDER.map((p) => [p, 0])) as Record<Product, number>;
    let shown = 0;
    let withDelay = 0;
    let onTime = 0;

    for (const vehicle of all) {
      counts[vehicle.line.product] += 1;
      if (!filters.has(vehicle.line.product) || !matchesLine(vehicle)) continue;
      shown += 1;
      if (vehicle.delay !== null) {
        withDelay += 1;
        if (vehicle.delay < 180) onTime += 1;
      }
    }

    filters.setCounts(counts);
    statusBar.set({
      shown,
      total: all.length,
      updatedAt: lastUpdatedAt,
      punctuality: withDelay >= 5 ? onTime / withDelay : null,
      message: null,
    });
  }

  const brandNetwork = el('brand-network');
  /** Nennt die Quellen, die zum aktuellen Bild beigetragen haben - sie wechseln
   *  mit dem Ausschnitt, seit es keine Netzauswahl mehr gibt. */
  const showSources = (ids: string[]): void => {
    const names = ids
      .map((id) => networks.find((n) => n.id === id)?.attribution)
      .filter((a): a is string => Boolean(a));
    brandNetwork.textContent = names.length ? names.join(' · ') : 'Live-Nahverkehr';
  };
  showSources([]);

  async function poll(): Promise<void> {
    pollRequest?.abort();
    const controller = new AbortController();
    pollRequest = controller;

    if (consecutiveErrors === 0) statusBar.set({ state: 'loading' });

    try {
      const update = await fetchArea(viewportBBox(), limitForZoom(), controller.signal);
      if (controller.signal.aborted) return;

      // Animate over slightly more than one interval so motion never stalls.
      vehicleLayer.update(update.vehicles, pollIntervalMs * 1.15);
      lastUpdatedAt = update.fetchedAt || Date.now();
      consecutiveErrors = 0;
      showSources(update.networks);
      if (update.notice) {
        // Ein Sachstand, kein Ausfall: sonst meldete die Karte "Offline", waehrend
        // der Dienst einwandfrei laeuft.
        statusBar.set({ state: 'loading', message: update.notice });
      } else {
        statusBar.set({ state: update.stale ? 'loading' : 'live', message: null });
        refreshStats();
      }

      if (selectedId) {
        const vehicle = vehicleLayer.getVehicle(selectedId);
        if (vehicle) details.updateVehicle(vehicle);
      }
      if (following) centerOnSelected(pollIntervalMs);
    } catch (err) {
      if (controller.signal.aborted || (err as Error)?.name === 'AbortError') return;
      consecutiveErrors += 1;
      statusBar.set({
        state: 'error',
        message:
          consecutiveErrors > 2
            ? `Keine Verbindung zu den Echtzeitdaten (${
                err instanceof Error ? err.message : String(err)
              })`
            : 'Verbindung wackelt, versuche erneut…',
      });
    } finally {
      if (pollRequest === controller) pollRequest = null;
    }
  }

  const pollSoon = debounce(() => void poll(), 350);

  // The first poll only needs the viewport, not a finished style.
  void poll();
  map.on('moveend', () => {
    if (programmaticMove) {
      programmaticMove = false;
      if (following) return;
    }
    updateHash();
    pollSoon();
  });
  window.setInterval(() => {
    if (!document.hidden) void poll();
  }, pollIntervalMs);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void poll();
  });

  const updateHash = debounce(() => {
    const center = map.getCenter();
    const next = `#${map.getZoom().toFixed(2)}/${center.lat.toFixed(4)}/${center.lng.toFixed(4)}`;
    if (location.hash !== next) history.replaceState(null, '', next);
  }, 400);

  // --- Map interaction -----------------------------------------------------

  const canvas = map.getCanvas();

  map.on('mousemove', (event) => {
    const vehicle = vehicleLayer.pick(event.point.x, event.point.y);
    const visible =
      vehicle && filters.has(vehicle.line.product) && matchesLine(vehicle) ? vehicle : null;
    vehicleLayer.setHovered(visible?.id ?? null);
    canvas.style.cursor = visible ? 'pointer' : '';
    if (visible) tooltip.show(visible, event.originalEvent.clientX, event.originalEvent.clientY);
    else tooltip.hide();
  });

  map.on('mouseout', () => {
    vehicleLayer.setHovered(null);
    tooltip.hide();
  });

  map.on('click', (event) => {
    const vehicle = vehicleLayer.pick(event.point.x, event.point.y);
    if (vehicle && !filters.has(vehicle.line.product)) return;
    selectVehicle(vehicle?.id ?? null);
  });

  map.on('movestart', () => tooltip.hide());

  // --- Chrome --------------------------------------------------------------


  const searchInput = el<HTMLInputElement>('search-input');
  let stopMarker: maplibregl.Marker | null = null;

  const dropStopMarker = (hit: StopHit | null): void => {
    stopMarker?.remove();
    stopMarker = null;
    if (!hit) return;
    const element = document.createElement('div');
    element.className = 'stop-marker';
    element.title = hit.area ? `${hit.name} · ${hit.area}` : hit.name;
    stopMarker = new maplibregl.Marker({ element })
      .setLngLat([hit.lon, hit.lat])
      .addTo(map);
  };

  /**
   * Linientreffer koennen nur aus den geladenen Fahrzeugen kommen - einen Suchdienst
   * fuer Linien gibt es bei keiner der Quellen. Je Linie wird das dem Kartenmittelpunkt
   * naechste Fahrzeug gemerkt, damit der Klick dorthin springen kann.
   */
  const lineHits = (needle: string): LineHit[] => {
    const center = map.getCenter();
    const groups = new Map<string, LineHit>();
    for (const vehicle of vehicleLayer.vehicles()) {
      const name = vehicle.line.name;
      if (!name.toLowerCase().includes(needle)) continue;
      const key = name.toLowerCase();
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          name,
          bg: vehicle.line.bg,
          fg: vehicle.line.fg,
          count: 1,
          vehicleId: vehicle.id,
          lat: vehicle.lat,
          lon: vehicle.lon,
        });
        continue;
      }
      existing.count += 1;
      const near = (lat: number, lon: number): number =>
        (lat - center.lat) ** 2 + (lon - center.lng) ** 2;
      if (near(vehicle.lat, vehicle.lon) < near(existing.lat, existing.lon)) {
        existing.vehicleId = vehicle.id;
        existing.lat = vehicle.lat;
        existing.lon = vehicle.lon;
      }
    }
    return [...groups.values()]
      .sort((a, b) => a.name.localeCompare(b.name, 'de', { numeric: true }))
      .slice(0, 6);
  };

  new SearchPanel(searchInput, el<HTMLButtonElement>('search-clear'), el('search-results'), {
    lineHits,
    near: () => [map.getCenter().lat, map.getCenter().lng],
    fetchStops,
    onStop: (hit) => {
      dropStopMarker(hit);
      programmaticMove = true;
      map.easeTo({ center: [hit.lon, hit.lat], zoom: 15, duration: 700 });
    },
    onLine: (hit) => {
      dropStopMarker(null);
      programmaticMove = true;
      map.easeTo({
        center: [hit.lon, hit.lat],
        zoom: Math.max(map.getZoom(), 13),
        duration: 700,
      });
      // Dasselbe wie ein Klick auf das Fahrzeug: Detailfenster und Streckenverlauf.
      // Der Verlauf ist fahrtbezogen, es gibt ihn also nur ueber ein Fahrzeug.
      // Die Auswahl raeumt den Linienfilter ab, deshalb wird er danach gesetzt.
      selectVehicle(hit.vehicleId);
      lineFilter = hit.name.toLowerCase();
      applyView();
      refreshStats();
    },
    onClear: () => {
      dropStopMarker(null);
      lineFilter = null;
      applyView();
      refreshStats();
    },
  });

  const colorToggle = el<HTMLButtonElement>('colormode-toggle');
  const colorLabel = el('colormode-label');
  const applyColorMode = (): void => {
    vehicleLayer.setColorMode(colorMode);
    colorToggle.setAttribute('aria-pressed', String(colorMode === 'delay'));
    colorLabel.textContent = colorMode === 'delay' ? 'Verspätung' : 'Linienfarbe';
    colorToggle.title =
      colorMode === 'delay'
        ? 'Fahrzeuge sind nach Verspätung eingefärbt — klicken für Linienfarben'
        : 'Fahrzeuge sind in Linienfarben — klicken für Verspätungsfarben';
  };
  applyColorMode();
  colorToggle.addEventListener('click', () => {
    colorMode = colorMode === 'line' ? 'delay' : 'line';
    savePrefs({ ...loadPrefs(), colorMode });
    applyColorMode();
  });

  const themeToggle = el<HTMLButtonElement>('theme-toggle');
  themeToggle.addEventListener('click', () => {
    theme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = theme;
    savePrefs({ ...loadPrefs(), theme });
    map.setStyle(styleUrl(theme));
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (details.isOpen) selectVehicle(null);
      else if (document.activeElement === searchInput) searchInput.blur();
      return;
    }
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
      return;
    }
    if (event.key === '/') {
      event.preventDefault();
      searchInput.focus();
      searchInput.select();
    }
  });
}

void boot();
